import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../../Admin/shared';
import { useFriendUsers } from '../api/useFriendUsers';
import type { FriendUserRow, UserSortKey } from '../api/types';

const PAGE_SIZE = 25;

interface ColumnDef {
  key: UserSortKey;
  label: string;
  /** Shown on hover. Used where the header alone could be misread. */
  title?: string;
  align?: 'left' | 'right';
}

const COLUMNS: ColumnDef[] = [
  { key: 'display_name', label: 'User', align: 'left' },
  {
    key: 'friends',
    label: 'Friends',
    title: "The user's current total friend count. Ignores the selected date range.",
    align: 'right',
  },
  {
    key: 'added',
    label: 'Added in period',
    title: 'Friendships formed inside the selected date range only — not the lifetime total.',
    align: 'right',
  },
  {
    key: 'initiated',
    label: 'Initiated',
    title: 'Accepted friend requests this user sent.',
    align: 'right',
  },
  {
    key: 'received',
    label: 'Received',
    title: 'Accepted friend requests this user received.',
    align: 'right',
  },
  { key: 'last_friend_at', label: 'Last friendship', title: 'When their most recent friendship was formed.', align: 'left' },
];

function fmtNum(v: number): string {
  return v.toLocaleString();
}

/** `null` means the user has never had a friend — an em-dash, never a bogus date. */
function fmtDate(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** `display_name` and `email` can both be null — fall back to the id, never blank or "null". */
function userLabel(row: FriendUserRow): { primary: string; secondary: string | null } {
  if (row.display_name) return { primary: row.display_name, secondary: row.email };
  if (row.email) return { primary: row.email, secondary: null };
  return { primary: row.user_id, secondary: null };
}

function SortArrow({ active, order }: { active: boolean; order: 'asc' | 'desc' }): React.ReactElement {
  if (!active) {
    return <span className="ml-1 text-gray-700 group-hover:text-gray-500">↕</span>;
  }
  return <span className="ml-1 text-violet-400">{order === 'asc' ? '▲' : '▼'}</span>;
}

export interface UserTableProps {
  from: string | null;
  to: string | null;
}

/**
 * The user-level drill-down for the friend graph: who has how many friends,
 * how many of those were added inside the selected window, and who
 * initiated. Sorting and paging are server-side — this component only
 * tracks which page and sort it currently wants and lets `useFriendUsers`
 * fetch that slice.
 */
export function UserTable({ from, to }: UserTableProps): React.ReactElement {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<UserSortKey>('friends');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error } = useFriendUsers({
    from: from ?? undefined,
    to: to ?? undefined,
    q: debouncedSearch || undefined,
    sort,
    order,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleSort = useCallback(
    (key: UserSortKey) => {
      setPage(0);
      // Compute the next sort/order from the CURRENT state up front, then
      // call each setter with a plain value -- never an updater function
      // with a side effect. React StrictMode (see src/index.tsx) double-
      // invokes state updaters in development; a setOrder call nested inside
      // setSort's updater would therefore toggle the order twice per click
      // and cancel itself out.
      const nextOrder = key === sort ? (order === 'asc' ? 'desc' : 'asc') : 'desc';
      setSort(key);
      setOrder(nextOrder);
    },
    [sort, order],
  );

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-200">Users</h2>
          <p className="mt-1 text-[11px] text-gray-500">Per-user friend counts and who initiated.</p>
        </div>
        <div className="relative w-full sm:w-64">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search name or email…"
            aria-label="Search users"
            className="w-full pl-9 pr-3 py-1.5 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-200 placeholder-gray-600 focus:outline-none focus:border-violet-500"
          />
        </div>
      </div>

      {error && (
        <div className="p-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {data && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-gray-700">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      title={col.title}
                      className={`group px-5 py-3 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap cursor-pointer select-none ${
                        col.align === 'right' ? 'text-right' : 'text-left'
                      } ${sort === col.key ? 'text-violet-300' : 'text-gray-500 hover:text-gray-300'} ${
                        col.title ? 'underline decoration-dotted decoration-gray-600 underline-offset-4' : ''
                      }`}
                    >
                      {col.label}
                      <SortArrow active={sort === col.key} order={order} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-5 py-10 text-center text-gray-500">
                      {search ? 'No users match your search.' : 'No users found.'}
                    </td>
                  </tr>
                )}
                {users.map((row) => {
                  const label = userLabel(row);
                  return (
                    <tr
                      key={row.user_id}
                      className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <Link
                          to={`/analytics/friends/${row.user_id}`}
                          className="text-gray-200 hover:text-violet-300 transition-colors"
                        >
                          <span className="font-medium">{label.primary}</span>
                          {label.secondary && (
                            <span className="block text-[11px] text-gray-500">{label.secondary}</span>
                          )}
                        </Link>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-200">
                        {fmtNum(row.friends_lifetime)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-200">
                        {fmtNum(row.friends_in_range)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-400">
                        {fmtNum(row.friends_initiated)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-400">
                        {fmtNum(row.friends_received)}
                      </td>
                      <td className="px-5 py-3 text-gray-400">{fmtDate(row.last_friend_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700 flex-wrap gap-2">
              <p className="text-[11px] text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + users.length, total)} of{' '}
                {total.toLocaleString()}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-gray-950 border border-gray-700 rounded-md hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  <span className="text-[11px] text-gray-500 tabular-nums">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-gray-950 border border-gray-700 rounded-md hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default UserTable;
